import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { CardPayment as MercadoPagoCardPayment, initMercadoPago, StatusScreen } from '@mercadopago/sdk-react';
import { API_BASE_URL } from '../../config/api';
import '../../styles/CardPayment.css';

let initializedPublicKey = null;

const CardPayment = ({
  curso,
  userData,
  publicKey,
  maxInstallments = 6,
  includesResume = true,
  onPaymentSuccess,
}) => {
  const [ready, setReady] = useState(false);
  const [brickReady, setBrickReady] = useState(false);
  const [renderAttempt, setRenderAttempt] = useState(0);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);
  // O SDK termina parte da renderização de forma assíncrona. O ID precisa
  // permanecer disponível inclusive durante uma nova tentativa.
  const brickContainerId = 'cardPaymentBrick_container';

  const resetPaymentForm = useCallback((message = '') => {
    setResult(null);
    setError(message);
    setBrickReady(false);
    setRenderAttempt((attempt) => attempt + 1);
  }, []);

  useEffect(() => {
    if (!publicKey) return;
    if (initializedPublicKey !== publicKey) {
      initMercadoPago(publicKey, { locale: 'pt-BR' });
      initializedPublicKey = publicKey;
    }
    setReady(true);
  }, [publicKey]);

  useEffect(() => {
    if (!ready || brickReady || result) return undefined;

    const timeout = window.setTimeout(() => {
      const container = document.getElementById(brickContainerId);
      if (!container || container.getBoundingClientRect().height < 100) {
        setError('O formulário seguro não carregou. Verifique sua conexão e tente novamente.');
      }
    }, 10000);

    return () => window.clearTimeout(timeout);
  }, [brickContainerId, brickReady, ready, result]);

  useEffect(() => {
    if (!result?.payment_id || result.status !== 'pending' || result.three_ds_info?.creq) return undefined;

    const interval = window.setInterval(async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await axios.post(
          `${API_BASE_URL}/payments/${result.payment_id}/check-status`,
          {},
          { headers: { Authorization: `Bearer ${token}` } },
        );
        if (response.data.status === 'completed') {
          window.clearInterval(interval);
          onPaymentSuccess?.(response.data);
        } else if (['failed', 'cancelled'].includes(response.data.status)) {
          window.clearInterval(interval);
          resetPaymentForm('O pagamento não foi aprovado. Tente novamente com outro cartão ou escolha o Pix.');
        }
      } catch (_) {
        // A próxima consulta automática tentará novamente.
      }
    }, 5000);

    const timeout = window.setTimeout(() => window.clearInterval(interval), 600000);
    return () => {
      window.clearInterval(interval);
      window.clearTimeout(timeout);
    };
  }, [onPaymentSuccess, resetPaymentForm, result]);

  const amount = Number(curso.price || 29.90);
  const initialization = useMemo(() => ({
    amount,
    payer: {
      email: userData.email,
    },
  }), [amount, userData.email]);

  const customization = useMemo(() => ({
    paymentMethods: {
      minInstallments: 1,
      maxInstallments,
      types: { included: ['credit_card'] },
    },
    visual: {
      texts: {
        formTitle: 'Dados do cartão',
        formSubmit: 'Pagar com cartão',
      },
    },
  }), [maxInstallments]);

  const handleSubmit = useCallback(async (formData) => {
    setError('');
    const token = localStorage.getItem('token');
    const deviceId = window.MP_DEVICE_SESSION_ID
      || document.getElementById('deviceId')?.value
      || '';

    try {
      const response = await axios.post(`${API_BASE_URL}/payment`, {
        curso_id: curso.id,
        includes_resume: includesResume,
        payment_method: 'credit_card',
        token: formData.token,
        installments: formData.installments,
        payment_method_id: formData.payment_method_id,
        issuer_id: formData.issuer_id || undefined,
        device_id: deviceId || undefined,
        payer: {
          identification: {
            type: formData.payer?.identification?.type,
            number: formData.payer?.identification?.number,
          },
        },
      }, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const payment = response.data;
      if (payment.status === 'completed') {
        setResult(payment);
        onPaymentSuccess?.(payment);
      } else if (!payment.success) {
        const message = payment.status_detail === 'cc_rejected_high_risk'
          ? 'O Mercado Pago bloqueou temporariamente novas tentativas por segurança. Aguarde antes de tentar novamente ou escolha o Pix.'
          : 'O pagamento não foi aprovado. Confira os dados e tente novamente ou use outro cartão.';
        resetPaymentForm(message);
      } else {
        setResult(payment);
      }
    } catch (requestError) {
      resetPaymentForm(
        requestError.response?.data?.error || 'Não foi possível processar o cartão. Tente novamente.',
      );
    }
  }, [curso.id, includesResume, onPaymentSuccess, resetPaymentForm]);

  const handleBrickReady = useCallback(() => {
    setBrickReady(true);
  }, []);

  const handleBrickError = useCallback((brickError) => {
    if (!brickError || brickError.type === 'critical') {
      setError('Não foi possível carregar o formulário do cartão.');
    }
  }, []);

  const retryBrick = useCallback(() => {
    resetPaymentForm();
  }, [resetPaymentForm]);

  if (!publicKey) {
    return <div className="card-payment-error">Pagamento por cartão indisponível no momento.</div>;
  }

  const threeDsInfo = result?.three_ds_info;
  const hasChallenge = result?.external_id && threeDsInfo?.external_resource_url && threeDsInfo?.creq;

  return (
    <div className="card-payment">
      <div className="card-payment-header">
        <div>
          <span className="card-payment-eyebrow">Pagamento seguro</span>
          <h3>Cartão de crédito</h3>
        </div>
        <div className="card-payment-amount">
          <strong>{amount.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}</strong>
          <span>Até {maxInstallments}x, com juros conforme o Mercado Pago</span>
        </div>
      </div>

      {error && (
        <div className="card-payment-error" role="alert">
          <span>{error}</span>
          {!result && <button type="button" onClick={retryBrick}>Tentar novamente</button>}
        </div>
      )}

      {hasChallenge ? (
        <StatusScreen
          initialization={{
            paymentId: String(result.external_id),
            additionalInfo: {
              externalResourceURL: threeDsInfo.external_resource_url,
              creq: threeDsInfo.creq,
            },
          }}
          customization={{ backUrls: { return: `${window.location.origin}/meus-certificados` } }}
          locale="pt-BR"
        />
      ) : ready && !result ? (
        <MercadoPagoCardPayment
          key={renderAttempt}
          id={brickContainerId}
          initialization={initialization}
          customization={customization}
          onSubmit={handleSubmit}
          onReady={handleBrickReady}
          onError={handleBrickError}
          locale="pt-BR"
        />
      ) : result?.status === 'pending' ? (
        <div className="card-payment-pending" role="status">
          Pagamento em análise. A confirmação será atualizada automaticamente.
        </div>
      ) : null}

      {ready && !result && !brickReady && !error && (
        <div className="card-payment-loading" role="status">Carregando formulário seguro...</div>
      )}
    </div>
  );
};

export default CardPayment;
